import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { initializeRecentTaskCNs, setSelectedTask } from 'slices/taskCNSlice'
import { FolderOpen } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'

function formatActivityDate(dateStr: string): string {
	if (!dateStr) return ''
	const d = new Date(dateStr)
	const now = new Date()
	const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const sevenDaysAgo = new Date(todayStart)
	sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

	if (d >= todayStart) {
		return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })
	}
	if (d >= sevenDaysAgo) {
		return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
	}
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function SidebarTasksCNRecent() {
	const dispatch = useDispatch()

	const { userInfo } = useSelector((state: any) => state.userLogin)
	const userId = userInfo?._id

	const { tasks, loading, recentsInitialized } = useSelector((state: any) => {
		const taskIds = state.taskCNs.recents || []
		const recentTasks = taskIds.map((id: string) => state.taskCNs.entities[id]).filter(Boolean)
		return {
			tasks: recentTasks,
			recentsInitialized: state.taskCNs.recentsInitialized,
			loading: state.taskCNs.isLoading,
		}
	})

	useEffect(() => {
		if (userId && !recentsInitialized) {
			dispatch(initializeRecentTaskCNs({ userid: userId }) as any)
		}
	}, [dispatch, userId, recentsInitialized])

	const isEmpty = tasks?.length === 0

	const handleTaskClick = (task: any) => {
		dispatch(setSelectedTask(task))
	}

	return (
		<div className="flex flex-col h-full">
			<div className="px-4 py-3 border-b border-gray-100">
				<div className="flex items-center gap-2">
					<svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
					<span className="text-sm font-semibold text-gray-900">Recent Activity</span>
					{tasks.length > 0 && (
						<span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
							{tasks.length}
						</span>
					)}
				</div>
			</div>
			<div className="flex-1 overflow-y-auto">
				{loading ? (
					<div className="flex items-center justify-center h-full p-4">
						<span className="text-sm text-gray-400">Loading...</span>
					</div>
				) : isEmpty ? (
					<div className="text-center py-6">
						<svg className="w-5 h-5 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
						<p className="text-sm text-gray-400">No activity yet</p>
					</div>
				) : (
					<div className="flow-root px-4 pt-3">
						<ul className="-mb-6">
							{tasks.map((task: any, idx: number) => {
								const isLast = idx === tasks.length - 1
								const projectName = task.projectid?.projectName
								return (
									<li key={task._id || idx} className="relative pb-6 last:pb-0">
										{!isLast && (
											<span
												className="absolute left-[15px] top-8 -ml-px h-full w-0.5 bg-gray-200"
												aria-hidden="true"
											/>
										)}
										<button
											type="button"
											onClick={() => handleTaskClick(task)}
											className="relative flex items-start gap-3 no-underline group w-full text-left bg-transparent border-none p-0 m-0 cursor-pointer"
										>
											<Avatar className="h-8 w-8 flex-shrink-0">
												{task.projectid?.projectAsset?.image ? (
													<>
														<AvatarImage src={task.projectid.projectAsset.image} alt={task.projectid.projectAsset.assetName || ''} />
														<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
															{(task.projectid.projectAsset.assetName || '?').charAt(0).toUpperCase()}
														</AvatarFallback>
													</>
												) : (
													<AvatarFallback className="bg-amber-50 text-amber-600">
														<FolderOpen className="w-3.5 h-3.5" />
													</AvatarFallback>
												)}
											</Avatar>
											<div className="min-w-0 flex-1 pt-0.5">
												<span className="text-sm font-medium text-gray-900 group-hover:text-[#0080fe] transition-colors">
													{task.taskName}
												</span>
												{projectName && (
													<p className="mt-0.5 text-xs text-gray-400">{projectName}</p>
												)}
												{task.updateNote && (
													<p className="mt-0.5 text-sm text-gray-500">{task.updateNote}</p>
												)}
												{task.updatedAt && (
													<p className="mt-0.5 text-xs text-gray-400">{formatActivityDate(task.updatedAt)}</p>
												)}
											</div>
										</button>
									</li>
								)
							})}
						</ul>
					</div>
				)}
			</div>
		</div>
	)
}

export default SidebarTasksCNRecent
